import React, { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { NavLink } from '~/lib/react-router-dom';

import CategoryIcon from '@mui/icons-material/Category';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';

import { NAVIGATION_LINK_IDS } from '~/consts/navigation';

import useNavigationLinks from '~/hooks/useNavigationLinks';
import useProductNavigationLinks from '~/hooks/useProductNavigationLinks';

import { UserType } from '~/types/responses/users';

import { filterNavigationLinksForTenant, isNavigationPathSelected } from '~/utils/navigationLinks';
import { openLabPortal } from '~/utils/openLabPortal';

export const SIDEBAR_MINI_WIDTH = 64;
export const SIDEBAR_MIN_EXPANDED_WIDTH = 200;
export const SIDEBAR_DEFAULT_EXPANDED_WIDTH = SIDEBAR_MIN_EXPANDED_WIDTH;
export const SIDEBAR_MAX_EXPANDED_WIDTH = 480;
const SIDEBAR_COLLAPSE_THRESHOLD = (SIDEBAR_MINI_WIDTH + SIDEBAR_MIN_EXPANDED_WIDTH) / 2;

export const SIDEBAR_TOP_OFFSET_CSS_VARIABLE = '--admin-sidebar-top-offset';

interface AdminSidebarProps {
  isExpanded: boolean;
  expandedWidth: number;
  tenant: App.Tenant;
  user: App.User | UserType | null | undefined;
  onToggle: () => void;
  onResize: (next: { isExpanded: boolean; expandedWidth: number }) => void;
}

function getChildLinks(navLink: App.NavigationLink) {
  if (!navLink.subLinks?.length) {
    return [];
  }

  return navLink.href ? [{ href: navLink.href, label: navLink.label }, ...navLink.subLinks] : navLink.subLinks;
}

function isLinkSelected(pathname: string, navLink: App.NavigationLink) {
  if (isNavigationPathSelected(pathname, navLink.href)) {
    return true;
  }

  return navLink.subLinks?.some((subLink) => isNavigationPathSelected(pathname, subLink.href)) ?? false;
}

function SidebarLink({ isExpanded, navLink }: { isExpanded: boolean; navLink: App.NavigationLink }) {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(isLinkSelected(pathname, navLink));
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const Icon = navLink.icon;
  const childLinks = getChildLinks(navLink);
  const hasChildren = childLinks.length > 0;
  const isSelected = isLinkSelected(pathname, navLink);
  const isLabLink = navLink.id === NAVIGATION_LINK_IDS.LAB;

  const closeCollapsedMenu = () => {
    setAnchorElement(null);
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (isLabLink) {
      event.preventDefault();
      void openLabPortal();
      return;
    }

    if (!hasChildren) {
      return;
    }

    event.preventDefault();

    if (isExpanded) {
      setIsOpen((currentValue) => !currentValue);
      return;
    }

    setAnchorElement(event.currentTarget);
  };

  return (
    <>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <Tooltip title={isExpanded ? '' : navLink.label} placement="right">
          <ListItemButton
            component={hasChildren || isLabLink ? 'button' : NavLink}
            to={hasChildren || isLabLink ? undefined : navLink.href}
            selected={isSelected}
            onClick={handleClick}
            sx={{
              minHeight: 48,
              justifyContent: isExpanded ? 'initial' : 'center',
              px: 2,
              textAlign: 'left',
              width: '100%',
            }}
          >
            <ListItemIcon
              sx={{
                color: 'inherit',
                justifyContent: 'center',
                minWidth: isExpanded ? 40 : 0,
              }}
            >
              {Icon ? <Icon /> : <MenuIcon />}
            </ListItemIcon>
            {isExpanded && <ListItemText primary={navLink.label} />}
            {isExpanded &&
              hasChildren &&
              (isOpen ? <ExpandLessIcon sx={{ ml: 'auto' }} /> : <ExpandMoreIcon sx={{ ml: 'auto' }} />)}
          </ListItemButton>
        </Tooltip>
      </ListItem>

      {isExpanded && hasChildren && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {childLinks.map((childLink) => (
              <ListItem key={`${navLink.label}-${childLink.href}`} disablePadding>
                <ListItemButton
                  component={NavLink}
                  to={childLink.href}
                  selected={isNavigationPathSelected(pathname, childLink.href)}
                  sx={{
                    pl: 7,
                  }}
                >
                  <ListItemText primary={childLink.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}

      {!isExpanded && hasChildren && (
        <Menu anchorEl={anchorElement} open={Boolean(anchorElement)} onClose={closeCollapsedMenu}>
          {childLinks.map((childLink) => (
            <MenuItem
              key={`${navLink.label}-${childLink.href}`}
              component={NavLink}
              to={childLink.href}
              selected={isNavigationPathSelected(pathname, childLink.href)}
              onClick={closeCollapsedMenu}
            >
              {childLink.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}

export default function AdminSidebar({
  isExpanded,
  expandedWidth,
  tenant,
  user,
  onToggle,
  onResize,
}: AdminSidebarProps) {
  const navigationLinks = useNavigationLinks(user);
  const productLinks = useProductNavigationLinks(user);
  const filteredNavigationLinks = useMemo(
    () => filterNavigationLinksForTenant(navigationLinks, tenant),
    [navigationLinks, tenant],
  );
  const sidebarLinks = useMemo<Array<App.NavigationLink>>(
    () => [
      {
        href: '',
        label: 'Products',
        icon: CategoryIcon,
        subLinks: productLinks,
      },
      ...filteredNavigationLinks,
    ],
    [filteredNavigationLinks, productLinks],
  );

  const sidebarWidth = isExpanded ? expandedWidth : SIDEBAR_MINI_WIDTH;
  const [isResizing, setIsResizing] = useState(false);
  const resizeStateRef = useRef({ startX: 0, startWidth: sidebarWidth, lastExpandedWidth: expandedWidth });

  const handleResizeStart = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      resizeStateRef.current = {
        startX: event.clientX,
        startWidth: sidebarWidth,
        lastExpandedWidth: expandedWidth,
      };
      setIsResizing(true);

      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        const { startX, startWidth, lastExpandedWidth } = resizeStateRef.current;
        const proposed = startWidth + (moveEvent.clientX - startX);

        if (proposed < SIDEBAR_COLLAPSE_THRESHOLD) {
          onResize({ isExpanded: false, expandedWidth: lastExpandedWidth });
          return;
        }

        const clamped = Math.min(SIDEBAR_MAX_EXPANDED_WIDTH, Math.max(SIDEBAR_MIN_EXPANDED_WIDTH, proposed));
        resizeStateRef.current.lastExpandedWidth = clamped;
        onResize({ isExpanded: true, expandedWidth: clamped });
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [expandedWidth, onResize, sidebarWidth],
  );

  const handleResizeDoubleClick = useCallback(() => {
    onResize({ isExpanded: true, expandedWidth: SIDEBAR_DEFAULT_EXPANDED_WIDTH });
  }, [onResize]);

  return (
    <Box
      component="aside"
      aria-label="Primary navigation"
      sx={{
        alignSelf: 'flex-start',
        bgcolor: 'background.paper',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        height: `calc(100vh - var(${SIDEBAR_TOP_OFFSET_CSS_VARIABLE}, 0px))`,
        overflow: 'hidden',
        position: 'sticky',
        top: `var(${SIDEBAR_TOP_OFFSET_CSS_VARIABLE}, 0px)`,
        transition: (theme) =>
          isResizing
            ? 'none'
            : theme.transitions.create('width', {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.sharp,
              }),
        width: sidebarWidth,
        zIndex: 1,
      }}
    >
      <List
        sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}
        subheader={
          isExpanded ? (
            <ListSubheader
              component="div"
              disableSticky
              sx={{
                fontWeight: 600,
              }}
            >
              Navigation
            </ListSubheader>
          ) : undefined
        }
      >
        {sidebarLinks.map((navLink) => (
          <SidebarLink key={`${navLink.label}-${navLink.href}`} isExpanded={isExpanded} navLink={navLink} />
        ))}
      </List>
      <Divider />
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexShrink: 0,
          justifyContent: isExpanded ? 'flex-end' : 'center',
          p: 1,
        }}
      >
        <IconButton
          aria-label={isExpanded ? 'Collapse navigation' : 'Expand navigation'}
          onClick={onToggle}
          size="small"
        >
          {isExpanded ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize navigation"
        onMouseDown={handleResizeStart}
        onDoubleClick={handleResizeDoubleClick}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 8,
          height: '100%',
          cursor: 'col-resize',
          borderRight: 1,
          borderColor: isResizing ? 'primary.main' : 'divider',
          backgroundColor: isResizing ? 'action.selected' : 'transparent',
          transition: (theme) =>
            theme.transitions.create(['background-color', 'border-color'], {
              duration: theme.transitions.duration.shortest,
            }),
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
          zIndex: 2,
        }}
      />
    </Box>
  );
}
